import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CpqFormulaBuilderService } from './cpq-formula-builder.service';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CpqSelectComponent, SelectType } from '../cpq-select/cpq-select.component';
import {
    Formula,
    FormulaBuilderLookup,
    SyntaxCheck,
    SyntaxCheckResponse,
    FormulaBuilderType,
    FormulaBuilderConfig,
    FormulaBuilderExpression,
    FormulaPlaceHolders,
    AttributeValue,
    ActionAttribute
} from './cpq-formula-builder.model';
import { MessagesService } from '../common/services/messages.service';
import { CpqTextareaComponent } from '../cpq-textarea/cpq-textarea.component';
import { CtxTagBuilderComponent } from './ctx-tag-builder/ctx-tag-builder.component';
import { TableListTagBuilderComponent } from './table-list-tag-builder/table-list-tag-builder.component';
import { CpqFormulaBuilderVisualizeDialogComponent } from './cpq-formula-builder-visualize-dialog/cpq-formula-builder-visualize-dialog.component';
import { TranslationService } from '../common/services/translation.service';

@Component({
    selector: 'cpq-formula-builder',
    templateUrl: 'cpq-formula-builder.component.html',
    providers: [CpqFormulaBuilderService],
    styleUrls: ['cpq-formula-builder.component.scss'],
    standalone: false
})

export class CpqFormulaBuilderComponent implements OnInit {

    @ViewChild('formulaTextAreaElement', { static: true }) formulaTextAreaElement: CpqTextareaComponent;
    @ViewChild('selectVariableAttribute') selectVariableAttribute: CpqSelectComponent;
    @ViewChild('selectVariableAttributeValue') selectVariableAttributeValue: CpqSelectComponent;
    public canVariableContainAttribute: boolean;
    public showParseValidationMessage: boolean;
    public isSpecialTagSelected: boolean;
    public specialTagTranslationKey: string;
    public actionTabTranslationKey: string;
    public actionStep1TranslationKey: string;
    public actionInsertButtonTranslationKey: string;
    public selectedVariable: FormulaBuilderExpression;
    public selectedCartAction: FormulaBuilderExpression;
    public initData: FormulaBuilderConfig;
    public SelectType = SelectType;
    public FormulaBuilderType = FormulaBuilderType;
    public formula: Formula = new Formula();
    public syntaxCheck: SyntaxCheck = new SyntaxCheck();
    public lookups: FormulaBuilderLookup = new FormulaBuilderLookup();
    public syntaxCheckResponse: SyntaxCheckResponse = new SyntaxCheckResponse();
    public previousFormula: string;

    constructor(private service: CpqFormulaBuilderService,
        public dialogRef: MatDialogRef<CpqFormulaBuilderComponent>,
        public constructCtxDialog: MatDialog,
        public constructTableListDialog: MatDialog,
        public messagesService: MessagesService,
        public translationService: TranslationService,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: FormulaBuilderConfig) {
            this.dialogRef.disableClose = true;
    }
    
    ngOnInit(): void {
        this.initData = this.data;

        this.initFormula();
        this.initTranslations();
    }

    insertOperator() {
        if (!this.formula.selectedOperation) {
            this.messagesService.showAlertDialog({ title: '', content: 'FormulaBuilder.AlertMsg.Operators' });
            return;
        }

        const text = this.lookups.operations.find(x => x.id === this.formula.selectedOperation);

        this.insertText(text.expression);
    }

    insertSequenceOperator() {
        const sequenceOperator = this.lookups.sequenceOperators.find(x => x.id === this.formula.selectedSequenceOperator);
        const seq = this.lookups.sequences.find(x => x.id === this.formula.selectedSequence);

        sequenceOperator.expression = sequenceOperator.expression.replace(FormulaPlaceHolders.sequencePlaceholder, seq.text);

        this.insertText(sequenceOperator.expression);
    }

    insertVariable() {
        let tag;

        const variable = this.lookups.variables.find(x => x.id === this.formula.selectedVariable);
        const attribute = this.lookups.attributes.find(x => x.id === this.formula.selectedAttribute);
        const attributeValue = this.lookups.attributeValues.find(x => x.id === this.formula.selectedAttributeValue);

        if (!variable || !variable.id || !variable.expression) {
            this.messagesService.showAlertDialog({ title: '', content: 'FormulaBuilder.AlertMsg.Variables' });
            return;
        } else if (variable.expression.indexOf(FormulaPlaceHolders.attributePlaceholder) > -1 && !attribute) {
            this.messagesService.showAlertDialog({ title: '', content: 'FormulaBuilder.AlertMsg.Attributes' });
            return;
        } else if (variable.expression.indexOf(FormulaPlaceHolders.attributeValuePlaceholder) > -1 && !attributeValue) {
            this.messagesService.showAlertDialog({ title: '', content: 'FormulaBuilder.AlertMsg.AttributeValues' });
            return;
        } else {
            if (variable.id === 16) {
                tag = this.getTag(variable, attribute ? attribute.text : null, attributeValue ? attributeValue.text : null);
            } else {
                tag = this.getTag(variable, attribute ? attribute.text : null, attributeValue ? attributeValue.value : null);
            }
        }

        const text = tag ? tag : this.getTag(variable);

        this.insertText(text);

        this.clearSelections();
    }

    insertCartAction() {
        let tag;

        const cartAction = this.lookups.cartActions.find(x => x.id === this.formula.selectedCartAction);
        const actionParameter = this.lookups.actionParameters.find(x => x.id === this.formula.selectedCartActionParameter);

        if (!cartAction) {
            this.messagesService.showAlertDialog({ title: '', content: 'FormulaBuilder.AlertMsg.Actions' });
            return;
        } else if (cartAction.expression.indexOf(FormulaPlaceHolders.parameterPlaceholder) > -1 && !actionParameter) {
            this.messagesService.showAlertDialog({ title: '', content: 'FormulaBuilder.AlertMsg.Attributes' });
            return;
        }

        if (actionParameter && cartAction.expression.indexOf(FormulaPlaceHolders.parameterPlaceholder) >= 0) {
            tag = cartAction.expression.replace(FormulaPlaceHolders.parameterPlaceholder, actionParameter.text);
        }
        else {
            tag = cartAction.expression;
        }

        this.insertText(tag);

        this.clearSelections();
    }

    checkIfAttributeValueContainsInvalidCharacters(selectedValue: ActionAttribute) {
        if(selectedValue.value.indexOf(',') > -1) {
            return true;
        }
        else if(selectedValue.value.indexOf(':') > -1) {
            return true;
        }
        else {
            return false;
        }
    }

    insertAction() {
        const action = this.lookups.actions.find(x => x.id === this.formula.selectedAction);

        if (!action) {
            this.messagesService.showAlertDialog({ title: '', content: this.initData.ftype === 2 ? 'FormulaBuilder.AlertMsg.Conditions' : 'FormulaBuilder.AlertMsg.Actions' });
            return;
        }
        if (this.formula.selectedActionAttributes.length === 0) {
            this.messagesService.showAlertDialog({ title: '', content: 'FormulaBuilder.AlertMsg.ActionAttributeValues' });
            return;
        }

        let tag = action.expression;
        let selectedActionAttributes = new Array<ActionAttribute>();
        this.formula.selectedActionAttributes.forEach(attribute => {
            let selectedValue = this.lookups.actionAttributeValues[attribute - 1];

            if(action.expression.indexOf(FormulaPlaceHolders.actionAttributeNameValuePlaceholder) > -1) {
                if(this.checkIfAttributeValueContainsInvalidCharacters(selectedValue)) {
                    this.lookups.actionAttributes[attribute - 1].selected = false;
                    this.messagesService.showAlertDialog({ title: '', content: this.translationService.translate('FormulaBuilder.AlertMsg.ActionAttributeValueContainInvalidCharacter') + selectedValue.text });
                    return;
                }
            }

            selectedActionAttributes.push(selectedValue);
        })

        if (action.expression.indexOf(FormulaPlaceHolders.actionAttributeSeparatorPlaceholder) > -1) {
            tag = action.expression.replace(
                FormulaPlaceHolders.actionAttributeSeparatorPlaceholder, this.getActionTagWithSeparator(selectedActionAttributes));
        } else if (action.expression.indexOf(FormulaPlaceHolders.actionAttributeNameValuePlaceholder) > -1) {
            tag = action.expression.replace(
                FormulaPlaceHolders.actionAttributeNameValuePlaceholder, this.getActionTagWithAttributeNameValue(selectedActionAttributes));
        } else {
            tag = action.expression.replace(
                FormulaPlaceHolders.actionAttributeNamePlaceholder, this.getActionTagWithAttributeName(selectedActionAttributes));
        }

        this.insertText(tag);

        this.clearSelections();
    }

    checkSyntax() {
        this.showParseValidationMessage = true;
        
        this.syntaxCheck.productId = this.initData.productId;
        this.syntaxCheck.formula = this.formula.conditionFormula;

        if(this.initData.formulaBuilderType === FormulaBuilderType.Cart) {
            this.service.checkCartTagSyntax(this.syntaxCheck).subscribe(response => {
                this.syntaxCheckResponse = response;
            });
        }
        else {
            this.service.checkSyntax(this.syntaxCheck).subscribe(response => {
                this.syntaxCheckResponse = response;
            });
        }
    }

    onSelectedVariableChange() {
        this.selectedVariable = this.lookups.variables.find(x => x.id === this.formula.selectedVariable);

        this.lookups.attributeValues = new Array<AttributeValue>();
        this.formula.selectedAttribute = null;
        this.formula.selectedAttributeValue = null;
        this.isSpecialTagSelected = false;

        this.selectVariableAttribute.deselect(false);
        this.selectVariableAttributeValue.deselect(false); 

        this.GetAttributeAndAttributeValues(this.selectedVariable);

        switch(this.selectedVariable && this.selectedVariable.text) {
            case 'CTX':
                this.isSpecialTagSelected = true;
                this.specialTagTranslationKey = 'syntaxchecker_HMTL_CTXBuilderButtonLabel';
                break;
            case 'List':
                this.isSpecialTagSelected = true;
                this.data.isTableTag = false;
                this.specialTagTranslationKey = 'syntaxchecker_HMTL_ListBuilderButtonLabel'
                break;
            case 'Table':
                this.isSpecialTagSelected = true;
                this.data.isTableTag = true;
                this.specialTagTranslationKey = 'syntaxchecker_HMTL_TableBuilderButtonLabel'
                break;
        }
    }

    onSelectedCartActionChange() {
        this.selectedCartAction = this.lookups.cartActions.find(x => x.id === this.formula.selectedCartAction);

        this.formula.selectedCartActionParameter = null;
        this.isSpecialTagSelected = false;

        switch(this.selectedCartAction && this.selectedCartAction.text) {
            case 'CTX':
                this.isSpecialTagSelected = true;
                this.specialTagTranslationKey = 'syntaxchecker_HMTL_CTXBuilderButtonLabel';
                break;
            case 'List':
                this.isSpecialTagSelected = true;
                this.data.isTableTag = false;
                this.specialTagTranslationKey = 'syntaxchecker_HMTL_ListBuilderButtonLabel'
                break;
            case 'Table':
                this.isSpecialTagSelected = true;
                this.data.isTableTag = true;
                this.specialTagTranslationKey = 'syntaxchecker_HMTL_TableBuilderButtonLabel'
                break;
            case 'CO_INFO':
            case 'CU_INFO':
                this.lookups.actionParameters = this.service.getUserParameters();
                break;
            case 'BT_INFO':
            case 'END_USER_INFO':
                this.lookups.actionParameters = this.service.getCustomerParameters();
                break;
            case 'CO_STINFO':
                this.lookups.actionParameters = this.service.getShipToParameters();
                break;
            case 'Custom_Field':
                this.service.getCustomFields().subscribe(response => {
                    this.lookups.actionParameters = response;
                });;
                break;
        }
    }

    constructSpecialTag() {
        switch((this.selectedVariable && this.selectedVariable.text) || (this.selectedCartAction && this.selectedCartAction.text)) {
            case 'CTX':
                this.constructCtx();
                break;
            case 'List':
            case 'Table':
                this.constructListOrTable();
                break;
        }

        this.clearSelections();
    }

    onSelectedAttributeChange() {
        this.formula.selectedAttributeValue = null;

        this.selectVariableAttributeValue.deselect(false);

        if(this.formula.selectedAttribute) {
            this.service.getAttributeValues(this.initData.productId, this.formula.selectedAttribute).subscribe(attributeValues => {
                this.lookups.attributeValues = attributeValues;
            });
        }
    }

    visualize() {
        this.dialog.open(CpqFormulaBuilderVisualizeDialogComponent, {
            data: {
                conditionalFormula: this.formula.conditionFormula
            }
        });
    }

    save() {
        this.dialogRef.close(this.formula.conditionFormula);
    }

    cancel() {
        this.dialogRef.close(this.previousFormula);
    }

    private insertText(text: string) {
        const textArea = this.formulaTextAreaElement.textarea.nativeElement;
        this.formula.conditionFormula =
            textArea.value.substring(0, textArea.selectionStart) +
            text +
            textArea.value.substring(textArea.selectionEnd);
    }

    private clearSelections() {
        this.formula.selectedAction = null;
        this.formula.selectedActionAttributes = new Array<number>();
        this.lookups.actionAttributes.forEach(x => x.selected = false);

        this.formula.selectedCartAction = null;
        this.formula.selectedCartActionParameter = null;

        this.formula.selectedVariable = null;
        this.formula.selectedAttribute = null;
        this.formula.selectedAttributeValue = null;
    }

    private initFormula() {
        this.previousFormula = this.data.formula ? this.data.formula : '';
        this.formula.conditionFormula = this.data.formula ? this.data.formula : '';

        this.lookups = this.service.getLookups(this.data);
    }

    private initTranslations() {
        if(this.initData.isTrigger) {            
            if(this.initData.ftype == 2) {
                this.actionTabTranslationKey = "syntaxchecker_HTML_Conditions";
                this.actionStep1TranslationKey = "FormulaBuilder_Conditions_Step1Message";
                this.actionInsertButtonTranslationKey = "FormulaBuilder_Conditions_InsertCondition";
            }
            else {
                this.actionTabTranslationKey = "syntaxchecker_HTML_Actions";
                this.actionStep1TranslationKey = "FormulaBuilder_Actions_Step1Message";
                this.actionInsertButtonTranslationKey = "syntaxchecker_HMTL_InsertActionButtonLabel";
            }
        }
    }

    private constructCtx() {
        this.constructCtxDialog.open(CtxTagBuilderComponent, {
            data: this.initData
        }).afterClosed().subscribe(response => {
            this.insertText(response);
          });
    }

    private constructListOrTable() {
        this.constructTableListDialog.open(TableListTagBuilderComponent, {
            data: this.initData
        }).afterClosed().subscribe(response => {
            this.insertText(response);
          });
    }

    private GetAttributeAndAttributeValues(variable: FormulaBuilderExpression) {
        this.canVariableContainAttribute = variable ? variable.canContainAttribute : false;

        if(this.canVariableContainAttribute) {
            if(this.initData.productId > 0) {
                this.service.getAttributes(this.initData.productId).subscribe(attributes => {
                    this.lookups.attributes = attributes;
                });
            }
        }
    }

    private getTag(variable: FormulaBuilderExpression, attributeName?: string, attributeValue?: string): string {
        const tag = attributeName ? variable.expression.replace(FormulaPlaceHolders.attributePlaceholder, attributeName) : variable.expression;
        return attributeValue ? tag.replace(FormulaPlaceHolders.attributeValuePlaceholder, attributeValue) : tag;
    }

    // Gets a value inside of parentheses of a tag for 'attributeName: val1 | val2 | val3' format
    private getActionTagWithSeparator(actionAttributes: Array<ActionAttribute>): string {
        let tag = '';

        // concatenation of tag
        actionAttributes.forEach(attribute => {
            if(tag.indexOf(attribute.name) > 0) {
                tag += ' | ' + attribute.value;
            }
            else {
                tag += ', ' + attribute.name + ': ' + attribute.value;
            }
        });

        // removing first two characters: ', '
        return tag.substring(2, tag.length);
    }

    // Gets a value inside of parentheses of a tag for 'attributeName:attributeValue' format
    private getActionTagWithAttributeNameValue(actionAttributes: Array<ActionAttribute>): string {
        let tag = '';

        // concatenation of tag
        actionAttributes.forEach(attribute => {
            tag += attribute.text + ','
        });

        // removing last character: ','
        return tag.substring(0, tag.length - 1);
    }

    // Gets a value inside of parentheses of a tag for 'attributeName' format
    private getActionTagWithAttributeName(actionAttributes: Array<ActionAttribute>): string {
        let tag = '';

        // concatenation of tag
        actionAttributes.forEach(attribute => {
            tag += attribute.name + ','
        })

        // removing last character: ','
        return tag.substring(0, tag.length - 1);
    }
}
